import type { Mode } from '@/types/entity-mode';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useEventStore } from './use-event-store';
import { getObjectId } from '@/helpers/object.id.helper';

type ModeState = {
  flowMode: Record<string, Mode>;
  eventMode: Record<string, Mode>;
};

export const useModeStore = create<ModeState & { setMode: (change: Partial<ModeState>) => void }>()(
  persist(
    (set, get) => ({
      flowMode: {},
      eventMode: {},
      setMode: (change) => {
        const state = get();
        const newState = { ...state, ...change };

        // If flowMode is being updated, check if we need to sync eventMode for single-flow events
        if (change.flowMode) {
          const eventStore = useEventStore.getState();
          const event = eventStore.event;

          // Check if this is a single-flow event
          if (event && !event.multiFlow) {
            const eventOriginId = event.origin ? getObjectId(event.origin) : event.id;

            // Find which flowId's mode changed
            const changedFlowIds = Object.keys(change.flowMode).filter(
              (flowId) => change.flowMode![flowId] !== state.flowMode[flowId],
            );

            // For each changed flow, sync the event mode if it's a single-flow event
            if (changedFlowIds.length > 0 && eventOriginId) {
              const newMode = change.flowMode[changedFlowIds[0]];

              newState.eventMode = {
                ...newState.eventMode,
                [eventOriginId]: newMode,
              };
            }
          }
        }

        set(newState);
      },
    }),
    {
      name: 'mode',
    },
  ),
);
